/* eslint-disable max-lines */
import Modal from '@components/atoms/modals/modal.tsx';
import { useState } from 'react';
import { useCreateFolder } from '@utils/api/folders/use-create-folder';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Permission, PermissionType } from '@utils/types/folders/folder';
import { useGetGroups } from '@utils/api/groups/use-get-groups';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { Eye, Lock, Pencil, Plus, X } from 'lucide-react';
import { AccessSelector } from '@components/workspace/access-selector.tsx';

type CreateFolderProps = {
  isOpen: boolean;
  onClose: () => void;
};

type Params = {
  folderId?: string;
};

export type TeamPermission = {
  groupId: string;
  permission: PermissionType;
};

export const CreateFolder = ({ isOpen, onClose }: CreateFolderProps) => {
  const { t } = useTranslation();
  const { getColour, getBrandTint, getContrastTextColor } = useThemeContext();
  const [isCreating, setIsCreating] = useState(false);
  const [folderName, setFolderName] = useState('');
  const createFolderMutation = useCreateFolder();
  const { folderId } = useParams<Params>();
  const [defaultAccess, setDefaultAccess] = useState(PermissionType.WRITE);
  const [teamPermissions, setTeamPermissions] = useState<TeamPermission[]>([]);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [addHovered, setAddHovered] = useState(false);

  const { data: groups } = useGetGroups({ enabled: isOpen });

  const activePillStyle = {
    backgroundColor: getColour('activeText'),
    borderColor: getColour('activeText'),
    color: getContrastTextColor(getColour('activeText'))
  };
  const inactivePillStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('hairline')
  };

  const permissionsList = [
    {
      id: PermissionType.NO_ACCESS,
      icon: <Lock className="h-3.5 w-3.5" />,
      description: t('no-access')
    },
    {
      id: PermissionType.READ,
      icon: <Eye className="h-3.5 w-3.5" />,
      description: t('read')
    },
    {
      id: PermissionType.WRITE,
      icon: <Pencil className="h-3.5 w-3.5" />,
      description: t('write')
    }
  ];

  const handleAddTeam = () => {
    setTeamPermissions(prev => [
      ...prev,
      { groupId: '', permission: PermissionType.WRITE }
    ]);
  };

  const handleRemoveTeam = (index: number) => {
    setTeamPermissions(prev => prev.filter((_, i) => i !== index));
  };

  const handleGroupChange = (
    index: number,
    option: { value: string; label: string }
  ) => {
    if (option) {
      setTeamPermissions(prev =>
        prev.map((tp, i) =>
          i === index ? { ...tp, groupId: option.value } : tp
        )
      );
    }
  };

  const handlePermissionChange = (index: number, permissionId: number) => {
    if (index === -1) {
      setDefaultAccess(permissionId);
    } else {
      setTeamPermissions(prev =>
        prev.map((tp, i) =>
          i === index ? { ...tp, permission: permissionId } : tp
        )
      );
    }
  };

  const handleSave = () => {
    if (!folderName.trim()) {
      toast.error(t('folder-name-is-required'));
      return;
    }

    if (
      defaultAccess === PermissionType.NO_ACCESS &&
      teamPermissions.some(tp => !tp.groupId.trim())
    ) {
      toast.error(t('please-select-a-group-for-all-teams'));
      return;
    }

    const folderIdInt = parseInt(folderId || '', 10);
    const permissionsValue: Permission[] =
      defaultAccess !== PermissionType.NO_ACCESS
        ? [{ group_id: null, permission: defaultAccess }]
        : teamPermissions.map(tp => ({
            group_id: parseInt(tp.groupId, 10),
            permission: tp.permission
          }));

    setIsCreating(true);
    createFolderMutation.mutate(
      {
        name: folderName,
        parent_id: isNaN(folderIdInt) ? null : folderIdInt,
        permissions: permissionsValue
      },
      {
        onSuccess: () => {
          toast.success(t('folder-created-successfully'));
          setIsCreating(false);
          onClose();
          setFolderName('');
          setTeamPermissions([]);
          setDefaultAccess(PermissionType.WRITE);
        },
        onError: () => {
          // Error toast is shown by the global mutation handler
          // (queryClient defaultOptions.mutations.onError); only reset
          // the loading state here to avoid a duplicate toast.
          setIsCreating(false);
        }
      }
    );
  };

  const isPrivate = defaultAccess === PermissionType.NO_ACCESS;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title={t('create-a-new-folder')}
      footerActions={[
        <Button
          key="cancel"
          onClick={onClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isCreating}
        />,
        <Button
          key="save"
          onClick={handleSave}
          label={isCreating ? t('creating') : t('create')}
          size="small"
          variant="primary"
          loading={isCreating}
        />
      ]}
    >
      <form
        className="grid gap-5 py-2"
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}
      >
        {/* Folder name */}
        <div>
          <label
            htmlFor="name"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('name')}
          </label>
          <Input
            id="name"
            placeholder={t('name')}
            onChange={e => setFolderName(e.target.value)}
            value={folderName}
            className="w-full"
          />
        </div>

        {/* Access — shared / private cards */}
        <div>
          <p
            className="mb-2 text-xs font-semibold uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('access')}
          </p>
          <AccessSelector
            isPrivate={isPrivate}
            onSelectShared={() => setDefaultAccess(PermissionType.WRITE)}
            onSelectPrivate={() => setDefaultAccess(PermissionType.NO_ACCESS)}
          />
        </div>

        {/* Team permission rows — only visible in private mode */}
        {isPrivate && (
          <div>
            <p
              className="mb-3 text-xs font-semibold uppercase tracking-wide"
              style={{ color: getColour('inactiveText') }}
            >
              {t('team-permissions')}
            </p>

            <div className="space-y-3">
              {teamPermissions.map((tp, index) => (
                <div
                  key={index}
                  className="relative rounded-xl border border-hairline bg-app-soft p-4"
                >
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveTeam(index)}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full transition-colors"
                    style={{
                      color:
                        hoveredRow === index
                          ? 'rgb(239, 68, 68)'
                          : getColour('inactiveText'),
                      backgroundColor:
                        hoveredRow === index
                          ? 'rgba(239, 68, 68, 0.1)'
                          : 'transparent'
                    }}
                  >
                    <X size={13} />
                  </button>

                  {/* Group select */}
                  <div className="pr-8">
                    <label
                      htmlFor={`group-${index}`}
                      className="mb-1.5 block text-xs font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('team')}
                    </label>
                    <SelectInput
                      id={`group-${index}`}
                      className="w-full text-sm"
                      options={(groups?.result || []).map(g => ({
                        label: g.name,
                        value: g.id.toString()
                      }))}
                      onChange={option => handleGroupChange(index, option)}
                      value={
                        (groups?.result || [])
                          .filter(g => g.id.toString() === tp.groupId)
                          .map(g => ({
                            label: g.name,
                            value: g.id.toString()
                          }))[0] ?? null
                      }
                      isMulti={false}
                    />
                  </div>

                  {/* Permission pills */}
                  <div className="mt-3">
                    <div
                      className="mb-1.5 text-xs font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('team-permissions')}
                    </div>
                    <div className="flex gap-2">
                      {permissionsList.map(p => (
                        <label
                          key={p.id}
                          className="group relative flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-full border py-2 text-xs font-semibold transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary"
                          style={
                            tp.permission === p.id
                              ? activePillStyle
                              : inactivePillStyle
                          }
                        >
                          <input
                            type="radio"
                            name={`permission-${index}`}
                            value={p.id}
                            checked={tp.permission === p.id}
                            onChange={() => handlePermissionChange(index, p.id)}
                            className="absolute inset-0 appearance-none cursor-pointer focus:outline-none"
                          />
                          <span className="shrink-0">{p.icon}</span>
                          <span>{p.description}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add team — dashed full-width trigger */}
            <button
              type="button"
              onClick={handleAddTeam}
              onMouseEnter={() => setAddHovered(true)}
              onMouseLeave={() => setAddHovered(false)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-all duration-200"
              style={{
                borderColor: addHovered
                  ? getColour('activeText')
                  : getColour('inactiveText'),
                color: addHovered
                  ? getColour('activeText')
                  : getColour('inactiveText'),
                backgroundColor: addHovered ? getBrandTint(0.04) : 'transparent'
              }}
            >
              <Plus size={15} />
              {t('add-team')}
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
};
